/**
 * Read-only reservoir development passport — one accordion per layer.
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { FieldProductionChart } from '@/components/Scenarios/FieldProductionChart';
import { VoronoiMbMap } from '@/components/Scenarios/VoronoiMbMap';
import type { LayerPassportViewModel, PassportKv } from '@/utils/materialBalanceLayerPassport';

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      fontWeight={700}
      sx={{ mt: 1.5, mb: 0.75, letterSpacing: 0.02 }}
    >
      {children}
    </Typography>
  );
}

function KvGrid({ rows, columns = 2 }: { rows: PassportKv[]; columns?: number }) {
  if (!rows.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gap: 0.75,
      }}
    >
      {rows.map((r, i) => (
        <Box
          key={`${r.label}-${i}`}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            px: 1,
            py: 0.5,
            bgcolor: 'action.hover',
            borderRadius: 0.75,
            minWidth: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" noWrap>
            {r.label}
          </Typography>
          <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'right' }}>
            {r.value}
            {r.unit ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {r.unit}
              </Typography>
            ) : null}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export interface MaterialBalanceLayerPassportProps {
  passport: LayerPassportViewModel;
  layerColor: string;
  /** Expand first layer with applied result by default */
  defaultExpanded?: boolean;
  /** When set, forces accordion open/closed (used during PDF capture). */
  forceExpanded?: boolean | null;
}

export const MaterialBalanceLayerPassport: React.FC<MaterialBalanceLayerPassportProps> = ({
  passport,
  layerColor,
  defaultExpanded = false,
  forceExpanded = null,
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const {
    layerName,
    status,
    headerKpis,
    summary,
    propsRows,
    diagnostics,
    wells,
    nProd,
    nInj,
    scheduleSpan,
    injectionNote,
    relpermSummary,
    relpermChart,
    forecast,
    forecastKpis,
    extractableOilKt,
    xDomain,
    contour,
    contourOrigin,
    mapWells,
    lastGrid,
    so0,
    hasResult,
  } = passport;

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderLeft: 4,
        borderLeftColor: layerColor,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Accordion
        expanded={forceExpanded === true ? true : expanded}
        onChange={(_, next) => {
          if (forceExpanded === true) return;
          setExpanded(next);
        }}
        disableGutters
        elevation={0}
        sx={{
          bgcolor: 'transparent',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 48,
            px: 1.5,
            '& .MuiAccordionSummary-content': {
              my: 1,
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              minWidth: 0,
            },
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: layerColor,
              flexShrink: 0,
            }}
          />
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ mr: 0.5 }}>
            {layerName}
          </Typography>
          <Chip
            size="small"
            label={status}
            color={status === 'ready' || hasResult ? 'success' : 'default'}
            variant="outlined"
            sx={{ height: 22, fontSize: 11 }}
          />
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ ml: { sm: 'auto' } }}>
            {headerKpis.map((k) => (
              <Typography
                key={k.label}
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap' }}
              >
                {k.label}{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {k.value}
                  {k.unit ? ` ${k.unit}` : ''}
                </Box>
              </Typography>
            ))}
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 1.5, pb: 2, pt: 0 }}>
          <SectionTitle>1. Summary</SectionTitle>
          <KvGrid rows={summary} columns={3} />

          <SectionTitle>2. Reserves and reservoir properties</SectionTitle>
          <KvGrid rows={propsRows} columns={3} />
          {diagnostics.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Simulation diagnostics
              </Typography>
              <KvGrid rows={diagnostics} columns={3} />
            </Box>
          )}

          <SectionTitle>3. Development system</SectionTitle>
          <KvGrid
            rows={[
              { label: 'Producers', value: String(nProd) },
              { label: 'Injectors', value: String(nInj) },
              { label: 'Schedule span', value: scheduleSpan },
              { label: 'Injection', value: injectionNote },
            ]}
            columns={2}
          />
          {wells.length > 0 ? (
            <Box sx={{ mt: 1, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Well</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell align="right">t0</TableCell>
                    <TableCell align="right">t1</TableCell>
                    <TableCell align="right">x (km)</TableCell>
                    <TableCell align="right">y (km)</TableCell>
                    <TableCell align="right">q start (t/d)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wells.map((w) => (
                    <TableRow key={w.id} hover>
                      <TableCell>{w.well_name}</TableCell>
                      <TableCell>{w.kind === 'inj' ? 'injector' : 'producer'}</TableCell>
                      <TableCell align="right">{w.t0 ?? '—'}</TableCell>
                      <TableCell align="right">{w.t1 ?? '—'}</TableCell>
                      <TableCell align="right">
                        {w.x != null ? w.x.toFixed(3) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {w.y != null ? w.y.toFixed(3) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {w.q_start_td != null ? w.q_start_td.toFixed(2) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              No wells on this layer.
            </Typography>
          )}

          <SectionTitle>4. Displacement / relative permeability</SectionTitle>
          <KvGrid rows={relpermSummary} columns={3} />
          {relpermChart.length > 0 && (
            <Box sx={{ height: 180, mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={relpermChart} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                  <XAxis
                    dataKey="sw"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tick={{ fontSize: 10 }}
                    label={{ value: 'Sw', position: 'insideBottom', offset: -2, fontSize: 10 }}
                  />
                  <YAxis type="number" domain={[0, 1]} tick={{ fontSize: 10 }} width={36} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="krw"
                    name="krw"
                    stroke="#1565c0"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="kro"
                    name="kro"
                    stroke="#c62828"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          <SectionTitle>5. Forecast</SectionTitle>
          {hasResult ? (
            <>
              <KvGrid rows={forecastKpis} columns={3} />
              <Box sx={{ mt: 1 }}>
                <FieldProductionChart
                  field={forecast}
                  xDomain={xDomain}
                  height={280}
                  label="Reservoir total"
                  extractableOilKt={extractableOilKt}
                  emptyHint="No applied result yet."
                />
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No applied result yet. Run material balance on Reservoir configuration for this layer.
            </Typography>
          )}

          <SectionTitle>6. Map snapshot</SectionTitle>
          {contour.length >= 3 && contourOrigin ? (
            <Box
              sx={{
                height: 320,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <VoronoiMbMap
                contour={contour}
                wells={mapWells}
                grid={lastGrid}
                so0={so0}
                origin={contourOrigin}
                height={320}
                fitKey={`${passport.layerId}-${contour.length}-${mapWells.length}`}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Contour or map datum missing — set contour on Reservoir configuration.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};
